import { useState, useEffect } from "react";
import { logError } from "@/lib/logError";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Globe, KeyRound, Loader2, ShieldCheck, LogIn, Flame, Fingerprint } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { loginBiometric, isBiometricAvailable } from "@/lib/webauthn";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGS } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { VideoDemoButton } from "@/components/VideoDemoButton";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/f2fded68-12b3-42fa-91bb-74fac756821c/images/0d706ad2f585dc5dd885bbe04a96ec26ca6df235a775722e404277415f8f864f.jpeg";

function PhoneFrame({ children }) {
  return (
    <div className="relative bg-black rounded-[2.8rem] p-2.5 shadow-2xl" style={{ width: 390, maxWidth: "94vw" }}>
      <div className="bg-white rounded-[2.3rem] overflow-hidden" style={{ height: 760, maxHeight: "86vh" }}>
        <div className="relative h-full overflow-y-auto">
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 h-6 w-28 bg-black rounded-full z-20" />
          <div className="px-7 pt-14 pb-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [testEnabled, setTestEnabled] = useState(false);
  useEffect(() => {
    api.get("/auth/test-login/enabled").then((r) => setTestEnabled(!!r.data.enabled)).catch(() => setTestEnabled(false));
  }, []);

  const [remember, setRemember] = useState(false);
  const [code, setCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);

  useEffect(() => { isBiometricAvailable().then(setBioAvailable).catch(logError("Détection biométrie")); }, []);

  const bioLogin = async () => {
    if (!email) { setError(lang === "fr" ? "Saisissez votre courriel pour la connexion biométrique" : "Enter your email for biometric login"); return; }
    setError(""); setBioBusy(true);
    try { const data = await loginBiometric(email); await finish(data); }
    catch (err) { setError(err?.response?.data?.detail || err?.message || "Échec de la connexion biométrique"); }
    finally { setBioBusy(false); }
  };

  const finish = async () => {
    localStorage.setItem("pi_last_activity", String(Date.now()));
    const me = await api.get("/auth/me");
    setUser(me.data);
    navigate("/");
  };
  const submitLogin = async (e) => {
    e.preventDefault(); setError(""); setInfo(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.totp_required) { setTempToken(data.temp_token); setStep("totp"); }
      else if (data.totp_setup_required) {
        setSetupToken(data.setup_token);
        const s = await api.post("/auth/totp/setup", { setup_token: data.setup_token });
        setQr(s.data.qr); setSecret(s.data.secret); setStep("setup");
      } else { await finish(data); }
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submitTotp = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { const { data } = await api.post("/auth/totp/verify", { temp_token: tempToken, code }); await finish(data); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submitEnable = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { const { data } = await api.post("/auth/totp/enable", { setup_token: setupToken, code }); await finish(data); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const forgot = async () => {
    if (!email) { setError(t("lg_courriel")); return; }
    try { await api.post("/auth/forgot-password", { email }); } catch (e) { logError("Mot de passe oublié")(e); }
    setInfo(lang === "fr" ? "Si un compte existe, un lien de réinitialisation a été envoyé." : "If an account exists, a reset link has been sent.");
  };
  const googleSoon = () => toast.info(t("lg_google_soon"));

  // ⚠️ Accès test (dev) — visible en aperçu ET en production (choix explicite du propriétaire)
  // ⚠️ Accès test (dev) — à retirer avant prod
  const TEST_ROLES = [
    ["admin_technique", "Administrateur technique"], ["responsable_qualite", "Responsable qualité"], ["directeur_medical", "Directeur médical"],
    ["chef_service", "Chef de service"], ["pathologiste", "Pathologiste"], ["pathologiste_distant", "Pathologiste distant"],
    ["expert_externe", "Pathologiste Expert"], ["technicien", "Technicien de laboratoire"], ["secretaire", "Secrétaire médicale"], ["clinicien", "Médecin clinicien"],
  ];
  const testLogin = async (role) => {
    setError(""); setBusy(true);
    try { const { data } = await api.post("/auth/test-login", { role }); await finish(data); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const card = (
    <div>
      <div className="flex justify-end">
        <button onClick={() => setLang(LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length])} data-testid="lang-toggle"
                className="flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-200">
          <Globe className="h-4 w-4" /> {lang.toUpperCase()}
        </button>
      </div>
      <div className="flex flex-col items-center text-center mt-2">
        <img src={LOGO} alt="Path-Intelligent" className="h-20 w-20 rounded-2xl shadow-md" data-testid="brand-logo" />
        <h1 className="font-serif-brand text-3xl mt-4 tracking-tight" style={{ color: "#1E3A6E" }}>Path-Intelligent</h1>
        <p className="text-sm font-bold mt-2 leading-snug" style={{ color: "#2563EB" }}>{t("lg_subtitle")}</p>
        <Link to="/fondation" title="Fondation Dr Ahlem AOUACHRI-MAKHLOUF"
             className="memorial-badge mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 ring-1 ring-amber-300/70 no-underline hover:ring-amber-400 transition-all"
             data-testid="memorial-login">
          <Flame className="h-4 w-4 memorial-icon" />
          <span className="memorial-shine text-sm italic tracking-wide">{t("lg_memorial")}</span>
          <Flame className="h-4 w-4 memorial-icon" />
        </Link>
        <div className="present-track mt-3 w-full" data-testid="card-presentation-track">
          <a href="/presentation.html" target="_blank" rel="noopener" data-testid="card-presentation-link"
             className="present-runner text-sm font-semibold underline underline-offset-2" style={{ color: "#2563EB" }}>
            Voir la présentation de la plateforme →
          </a>
        </div>
        <div className="mt-4 lg:hidden"><VideoDemoButton testId="card-video-demo-button" /></div>
      </div>

      <div className="mt-6">
        {step === "login" && (
          <form onSubmit={submitLogin} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="font-bold text-slate-800">{t("lg_courriel")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="login-email-input" placeholder="prenom@laboratoire.ca" className="pl-10 h-12 rounded-xl" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="font-bold text-slate-800">{t("lg_mdp")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="password" type={showPwd ? "text" : "password"} required value={password}
                       onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
                       placeholder="••••••••••" className="pl-10 pr-10 h-12 rounded-xl" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} data-testid="toggle-password-visibility"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-me-checkbox" /> {t("lg_remember")}
            </label>
            <button type="button" onClick={forgot} data-testid="forgot-password-link"
                    className="text-sm font-semibold block" style={{ color: "#2563EB" }}>{t("lg_forgot")}</button>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            {info && <p className="text-sm text-emerald-600" data-testid="login-info">{info}</p>}
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
                    className="w-full h-12 rounded-xl text-base font-semibold shadow-md" style={{ background: "#1E3A6E" }}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("lg_signin")}
            </Button>
            <div className="flex items-center gap-3 pt-1">
              <div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">{t("lg_or")}</span><div className="flex-1 h-px bg-slate-200" />
            </div>
            <button type="button" onClick={googleSoon} data-testid="google-signin-button"
                    className="w-full h-12 rounded-xl border border-slate-300 bg-white flex items-center justify-center gap-2 font-semibold text-slate-700 hover:bg-slate-50">
              <LogIn className="h-4 w-4" /> {t("lg_google")}
            </button>
            {bioAvailable && (
              <button type="button" onClick={bioLogin} disabled={bioBusy} data-testid="biometric-login-button"
                      className="w-full h-12 rounded-xl border border-sky-300 bg-sky-50 flex items-center justify-center gap-2 font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-50">
                {bioBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}
                {lang === "fr" ? "Connexion biométrique" : "Biometric login"}
              </button>
            )}

            <div className="pt-5 mt-2 border-t border-slate-100 text-center space-y-1.5">
              <p className="text-sm italic text-slate-500">{t("lg_memorial2024")}</p>
              <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-teal-600" /> {t("lg_hosting")}
              </p>
              <p className="text-xs" style={{ color: "#2563EB" }}>
                <a href="/conformite" className="underline">{t("lg_policy")}</a>
                <span className="text-slate-400"> · </span>
                <a href="/conformite" className="underline">{t("lg_terms")}</a>
              </p>
              <p className="text-[11px] text-slate-400 leading-snug px-2">{t("lg_loi25")}</p>
            </div>
          </form>
        )}

        {step === "totp" && (
          <form onSubmit={submitTotp} className="space-y-4" data-testid="totp-form">
            <div className="flex items-center justify-center gap-2 text-slate-900">
              <KeyRound className="h-5 w-5 text-sky-600" /><h2 className="font-head text-lg font-bold">{t("lg_verif_title")}</h2>
            </div>
            <p className="text-sm text-slate-500 text-center">{t("lg_verif_desc")}</p>
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)}
                   data-testid="login-totp-code-input" placeholder="000000"
                   className="text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-xl" />
            {error && <p className="text-sm text-red-600" data-testid="totp-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="totp-verify-button"
                    className="w-full h-12 rounded-xl font-semibold" style={{ background: "#1E3A6E" }}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("lg_verify")}
            </Button>
          </form>
        )}

        {step === "setup" && (
          <form onSubmit={submitEnable} className="space-y-3" data-testid="totp-setup-form">
            <h2 className="font-head text-lg font-bold text-slate-900 text-center">{t("lg_setup_title")}</h2>
            <p className="text-sm text-slate-500 text-center">{t("lg_setup_desc")}</p>
            {qr && <img src={qr} alt="QR TOTP" className="mx-auto h-40 w-40 border rounded-lg" data-testid="totp-qr" />}
            <p className="text-xs text-center text-slate-500">{t("lg_key")} : <span className="font-mono">{secret}</span></p>
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)}
                   data-testid="totp-setup-code-input" placeholder="000000"
                   className="text-center text-xl tracking-[0.4em] font-mono h-14 rounded-xl" />
            {error && <p className="text-sm text-red-600" data-testid="setup-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="totp-enable-button"
                    className="w-full h-12 rounded-xl font-semibold" style={{ background: "#1E3A6E" }}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("lg_enable")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 text-white" style={{ background: "#0F2942" }}>
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="logo" className="h-11 w-11 rounded-lg" />
          <div>
            <div className="font-head text-xl font-bold tracking-tight">Path-Intelligent</div>
            <div className="text-xs text-sky-200">{t("lg_systeme")}</div>
          </div>
        </div>
        <div>
          <h1 className="font-head text-5xl font-bold leading-tight mb-5">{t("lg_left1")}<br />{t("lg_left2")}</h1>
          <p className="text-slate-300 text-sm max-w-md leading-relaxed">{t("lg_left_desc")}</p>
          <div className="flex items-center gap-2 mt-8 text-xs text-sky-200">
            <ShieldCheck className="h-4 w-4" /> {t("lg_chiffre")}
          </div>
          <div className="present-track mt-5" data-testid="login-presentation-track">
            <a href="/presentation.html" target="_blank" rel="noopener" data-testid="login-presentation-link"
               className="present-runner text-sm font-semibold text-sky-300 hover:text-sky-100 underline underline-offset-2">
              Voir la présentation de la plateforme →
            </a>
          </div>
          <div className="mt-6 mb-8"><VideoDemoButton testId="login-video-demo-button" /></div>
        </div>
        <div className="w-full" data-testid="memorial-projet">
          <div className="flex items-center gap-3">
            <Link to="/fondation" title="Fondation Dr Ahlem AOUACHRI-MAKHLOUF" data-testid="memorial-portrait-link">
              <img src="/ahlem-portrait.jpg" alt="Dr Ahlem"
                   className="h-14 w-14 rounded-full object-cover ring-2 ring-amber-300/70 shadow-lg shadow-black/30 hover:ring-amber-400 transition-all cursor-pointer" />
            </Link>
            <Link to="/fondation" data-testid="memorial-foundation-cta" className="flex items-center gap-2 no-underline">
              <Flame className="h-4 w-4 memorial-icon" />
              <span className="memorial-shine text-xs tracking-wide">Projet mémoriel</span>
            </Link>
            <Link to="/fondation" title="Fondation Dr Ahlem AOUACHRI-MAKHLOUF" data-testid="memorial-logo-link" className="flex items-center gap-2 no-underline group ml-auto shrink-0">
              <span className="memorial-shine text-xs tracking-wide group-hover:underline">Fondation Dr Ahlem →</span>
              <img src="/foundation-logo.png" alt="Logo Fondation Dr Ahlem" className="h-14 w-14 rounded-full bg-white p-1 ring-2 ring-amber-300/60 object-contain" />
            </Link>
          </div>
          <p className="mt-2 text-[11px] italic text-slate-400 leading-snug max-w-sm" data-testid="memorial-citation">
            « À la mémoire du Dr Ahlem — sa rigueur, sa douceur et son dévouement envers ses patients continuent d'inspirer cette plateforme. »
          </p>
        </div>
        {testEnabled && <div className="mt-6 border border-sky-800/50 rounded-xl p-3 bg-sky-950/40 max-w-md" data-testid="test-access-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-amber-300">⚡ Accès test (sans TOTP) — temporaire</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {TEST_ROLES.map(([r, lbl]) => (
              <button key={r} disabled={busy} onClick={() => testLogin(r)} data-testid={`test-login-${r}`}
                      className="text-[11px] text-sky-100 bg-sky-800/60 hover:bg-sky-700 rounded-md px-2 py-1.5 text-left transition-colors disabled:opacity-50">
                {lbl}
              </button>
            ))}
          </div>
        </div>}
      </div>
      <div className="flex-1 flex items-center justify-center p-6" style={{ background: "#D1D5DB" }}>
        <PhoneFrame>{card}</PhoneFrame>
      </div>
    </div>
  );
}
